import "./styles/tailwind.css";
import "./i18n";
import "./Polyfills";
import * as Sentry from "@sentry/react";
import * as Apollo from "./Apollo";
import React, { ReactElement, Suspense, lazy } from "react";
import styled, { createGlobalStyle, css, ThemeProvider } from "styled-components";
import { absoluteCentre, defaultTheme, margin } from "./common/styles";
import { BookingToolData } from "./BookingToolData";
import { BrandStyleTag, LoadingSpinnerSvg as BaseLoadingSpinnerSvg } from "./common/components";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { GenericBookingToolData } from "./GenericBookingToolData";
import RootContainer from "./RootContainer";
const CancelFlow = lazy(() => import("./Cancel/CancelFlow"));
export const apolloClient = Apollo.createClient();
const urlParams = new URLSearchParams(window.location.search);
const inPopoverMode = !!urlParams.get("inPopoverMode");
export function App(): ReactElement {
  const urlParams = new URLSearchParams(window.location.search);
  const id = urlParams.get("id") ?? "";
  const organisationId = urlParams.get("organisationId") ?? "";
  const iFrameId = urlParams.get("iFrameId") ?? undefined;
  const pathname = window.location.pathname;
  const cancelMatch = pathname.match(/^\/cancel\/(.+)$/);
  if (cancelMatch) {
    const token = decodeURIComponent(cancelMatch[1]);
    return <ThemeProvider theme={defaultTheme}>
        <BrandStyleTag theme={defaultTheme} />
        <Suspense fallback={<LoadingSpinnerSvg title="loading" />}>
          <GlobalStyle isEmbedded={!!iFrameId} />
          <AppContainer inPopoverMode={false}>
            <CancelFlow token={token} />
          </AppContainer>
        </Suspense>
      </ThemeProvider>;
  }
  Sentry.setTags({
    "booking-tool.id": id ? id : "undefined",
    "iframe.id": iFrameId ? iFrameId : "undefined",
    "organisation.id": organisationId ? organisationId : "undefined"
  });
  return <ThemeProvider theme={defaultTheme} data-sentry-element="ThemeProvider" data-sentry-component="App" data-sentry-source-file="App.tsx">
      <BrandStyleTag theme={defaultTheme} data-sentry-element="BrandStyleTag" data-sentry-source-file="App.tsx" />
      {/* Cannot translate spinner title as it shows while translations are loading */}
      <Suspense fallback={<LoadingSpinnerSvg title="loading" />} data-sentry-element="Suspense" data-sentry-source-file="App.tsx">
        <GlobalStyle isEmbedded={!!iFrameId} data-sentry-element="GlobalStyle" data-sentry-source-file="App.tsx" />
        <Apollo.ApolloProvider client={apolloClient} data-sentry-element="unknown" data-sentry-source-file="App.tsx">
          <RootContainer inPopoverMode={inPopoverMode} data-sentry-element="RootContainer" data-sentry-source-file="App.tsx">
            <AppContainer inPopoverMode={inPopoverMode} data-sentry-element="AppContainer" data-sentry-source-file="App.tsx">
              {id && <BookingToolData inPopoverMode={inPopoverMode} />}
              {!id && organisationId && <GenericBookingToolData inPopoverMode={inPopoverMode} />}
            </AppContainer>
          </RootContainer>
        </Apollo.ApolloProvider>
      </Suspense>
    </ThemeProvider>;
}
const LoadingSpinnerSvg = styled(BaseLoadingSpinnerSvg)`
  ${absoluteCentre}
`;
const GlobalStyle = createGlobalStyle<{
  isEmbedded: boolean;
}>`
  html {
    font-size: 100%;  // 16px in most default cases
    -webkit-text-size-adjust: 100%;
  }

  :root {
    box-sizing: border-box;
    /*
     * Falls back to Lato when no brand font is configured (--d-font-family
     * is only set by theme.ts/BrandStyleTag when brand.fontFamily is
     * present). Without this fallback var(), a configured brand font
     * (e.g. Google Fonts loaded via BrandStyleTag's <link>) is computed
     * and injected as a CSS custom property but never actually applied to
     * any text anywhere in the app.
     */
    font-family: var(--d-font-family, Lato);
  }

  *, *:before, *:after {
    box-sizing: inherit;
  }

  html, body, #main {
    ${margin.zero}
    /*
     * Was margin: 0 only, with no height rule anywhere on this chain. In
     * full-page (non-popover) embed mode, AppContainer/RootContainer's
     * Container/ContentContainer's Container all rely on "height: 100%"
     * cascading down to fill the viewport with the app's themed
     * background — but a percentage height only resolves against an
     * ancestor with a DEFINITE height, and html/body/#main had none, so
     * the whole chain collapsed to auto/content-height instead. Any time
     * the app's actual content was shorter than the viewport, that left
     * the raw unstyled white document background exposed below it.
     *
     * "height: 100%" on html is a special case that resolves against the
     * viewport (the initial containing block) even with no explicit
     * ancestor, which is what makes the classic html/body/#main
     * "height: 100%" chain work reliably for filling the viewport.
     * "min-height: 100vh" is added as a belt-and-braces fallback on each
     * of the three (rather than relying solely on the percentage chain
     * staying unbroken) since it's an absolute viewport unit that doesn't
     * depend on any ancestor's height resolving correctly. Popover mode is
     * unaffected either way — RootContainer's Container switches to
     * "position: fixed" with all four inset offsets set in that mode, so
     * it already sizes to the viewport regardless of this chain.
     *
     * This must NOT be applied when embedded via the SDK's iframe (see
     * "isEmbedded", derived from the presence of the "iFrameId" URL param
     * set by renderWidget.tsx for every SDK-rendered iframe). Both
     * "height: 100%" on <html> and "min-height: 100vh" resolve against the
     * iframe's own current rendered viewport height — i.e. whatever height
     * iframe-resizer last set on the iframe element in the parent page.
     * With this rule in place, html/body/#main can never report a height
     * smaller than that previous value, so iframe-resizer can never
     * detect a shrink between steps: it's a self-reinforcing ratchet that
     * locks the iframe at the tallest height any step has ever needed.
     * Omitting it when embedded lets the height:100% chain below
     * (RootContainer → AppContainer → ContentContainer → Steps) correctly
     * collapse to natural content height with no definite ancestor height
     * to resolve against, so iframe-resizer can size the iframe accurately
     * on every step, including shrinking it.
     */
    ${({
  isEmbedded
}) => !isEmbedded && css`
        height: 100%;
        min-height: 100vh;
      `}
  }

`;
function getPopoverStyles(inPopoverMode: boolean) {
  if (inPopoverMode) {
    return css`
      ${absoluteCentre}

      @media screen and (width >= 800px) {
        width: 800px;
        max-height: 640px;
      }
      @media screen and (width >= 1200px) {
        width: 1072px;
        max-height: 800px;
      }
    `;
  }
  return null;
}
const AppContainer = styled.div<{
  inPopoverMode: boolean;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: visible;

  ${({
  inPopoverMode
}) => getPopoverStyles(inPopoverMode)};

  /*
   * Left transparent (rather than a hardcoded white) so that a brand-themed
   * popover shell (see ContentContainer's Container, which fills this 100%
   * and owns the actual surface colour / border / radius / shadow) isn't
   * fighting a solid white rectangle behind its rounded corners on dark
   * themes.
   */
  background-color: transparent;
`;
export default App;